import { TYPE_LABEL_MAP } from "src/assets/scripts/utilities/helpers";
import { capitalCase, snakeCase } from "change-case";

const withDrawer = (query, options) =>
  options?.openDrawer ? { ...query, openDrawer: true } : query;

const resolveNumberQuery = (record, fallbacks = []) => {
  const keys = ["number", "internal_number", ...fallbacks];

  for (const key of keys) {
    const value = record?.[key];

    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }

  return undefined;
};

const ROLE_HASH_MAP = {
  buyer: "#customers",
  seller: "#vendors",
};

const buildUserRoute = (record, role, options = {}) => {
  return {
    path: "/users",
    ...(ROLE_HASH_MAP[role] ? { hash: ROLE_HASH_MAP[role] } : {}),
    query: withDrawer(
      { number: record?.gstin || record?.name || record?.shop_name },
      options,
    ),
  };
};

const buildNoteRoute = (record, options, noteType, metaKey) => {
  const noteFor = record?.[metaKey] || record?.meta_data?.[metaKey];
  const isInvoice = noteFor === "invoice";

  const path = isInvoice ? `/sales/${noteType}` : `/purchases/${noteType}`;

  return {
    path,
    query: withDrawer({ number: record.number }, options),
  };
};

const CATALOGUE_HASH_MAP = (record) =>
  record?.meta_data?.hsn || record?.hsn ? "#products" : "#brands";

const ROUTE_BUILDERS = {
  Invoices: (record, options = {}) => ({
    path: "/sales/invoices",
    query: withDrawer(
      {
        number: resolveNumberQuery(record, [
          "Invoice Number",
          "invoice_number",
        ]),
      },
      options,
    ),
  }),

  Payments: (record, options = {}) => ({
    path: "/accounts/payment-requests",
    query: withDrawer(
      { number: record.internal_number || record.number },
      options,
    ),
  }),

  "Purchase Orders": (record) => {
    const id =
      (typeof record === "string" || typeof record === "number"
        ? record
        : null) ||
      record?._id ||
      record?.id ||
      record?.po_id ||
      record?.purchase_order_id;

    if (!id) {
      return null;
    }

    return {
      path: `/sales/purchase-orders/${id}`,
    };
  },

  "Proof of Deliveries": (record, options = {}) => ({
    path: "/sales/proof-of-deliveries",
    query: withDrawer({ number: record.number }, options),
  }),

  "Purchase Bills": (record, options = {}) => ({
    path: "/purchases/purchase-bills",
    query: withDrawer(
      {
        number: resolveNumberQuery(record, ["Bill Number", "Number"]),
        entityId:
          record?.entity_id ||
          record?._id ||
          record?.id ||
          record?.purchase_bill_id ||
          undefined,
      },
      options,
    ),
  }),

  "E-Way Bill": (record, options = {}) => ({
    path: "/shipping/e-way-bills",
    query: withDrawer(
      { number: record.number || record.internal_number },
      options,
    ),
  }),

  "Credit Notes": (record, options = {}) =>
    buildNoteRoute(record, options, "credit-notes", "cn_for"),

  "Debit Notes": (record, options = {}) =>
    buildNoteRoute(record, options, "debit-notes", "dn_for"),

  Users: (record, options = {}) =>
    buildUserRoute(record, record.meta_data?.role || record?.role, options),

  User: (record, options = {}) =>
    buildUserRoute(record, record?.role || record.meta_data?.role, options),

  Customers: (record, options = {}) => buildUserRoute(record, "buyer", options),

  Vendors: (record, options = {}) => buildUserRoute(record, "seller", options),

  Products: (record, options = {}) => ({
    path: "/catalogue",
    hash: CATALOGUE_HASH_MAP(record),
    query: withDrawer(
      { number: record.name || record.internal_name || record.hsn },
      options,
    ),
  }),
};

export const generateRoute = (label, record, options = {}) => {
  if (!label) {
    return null;
  }

  const cleanLabel = String(label).trim();
  const directBuilder = ROUTE_BUILDERS[cleanLabel];

  if (directBuilder) {
    return directBuilder(record, options);
  }

  const singularize = (value) => {
    if (value.endsWith("ies")) {
      return value.slice(0, -3) + "y";
    }

    if (value.endsWith("s")) {
      return value.slice(0, -1);
    }

    return value;
  };

  const snake = snakeCase(cleanLabel);
  const typeKey = singularize(snake);
  const mappedLabel = TYPE_LABEL_MAP[typeKey];

  if (mappedLabel && ROUTE_BUILDERS[mappedLabel]) {
    return ROUTE_BUILDERS[mappedLabel](record, options);
  }

  const titleCaseLabel = capitalCase(cleanLabel);

  if (ROUTE_BUILDERS[titleCaseLabel]) {
    return ROUTE_BUILDERS[titleCaseLabel](record, options);
  }

  const titleCaseFromType = capitalCase(mappedLabel || "");

  if (titleCaseFromType && ROUTE_BUILDERS[titleCaseFromType]) {
    return ROUTE_BUILDERS[titleCaseFromType](record, options);
  }

  return null;
};
